import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthLayout } from '@/layouts/AuthLayout';
import { RequireSession, RequireSuperAdmin } from '@/routes/RequireAuth';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import { PATIENT_PORTAL_ROUTE } from '@/features/patients/portalLink';
import { LoginPage } from '@/routes/pages/LoginPage';
import { RegisterPage } from '@/routes/pages/RegisterPage';
import { ForgotPasswordPage } from '@/routes/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/routes/pages/ResetPasswordPage';
import { CreateDispensaryPage } from '@/routes/pages/CreateDispensaryPage';
import { SelectClinicPage } from '@/routes/pages/SelectClinicPage';
import {
  PatientPortalPage,
  PlatformClinicDetailPage,
  PlatformClinicsPage,
  PlatformPasswordResetPage,
  PlatformSuperAdminsPage,
} from '@/routes/pages';
import { LegacyPublicRedirect } from '@/routes/LegacyPublicRedirect';
import { CLINIC_PICKER_PATH } from '@/routing/tenant';

/**
 * The routes that belong to no clinic, served at the root with no basename.
 *
 * Everything here is either pre-clinic (you have not signed in, or you have no
 * clinic yet) or deliberately clinic-agnostic. Each path is also listed in
 * `RESERVED_SLUGS` in `@synapse/clinic-slug` — that is what stops a clinic from
 * taking a slug that would shadow one of these. Adding a route here means adding
 * it there in the same change.
 */
export function GlobalRoutes() {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        {/* Password recovery. Global, and necessarily so: an account belongs to
            a person rather than to a clinic, and the link arrives in an email
            built from PUBLIC_BASE_URL with no clinic in it. Both paths are in
            RESERVED_SLUGS. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>
      {/* The clinic picker. Global because it exists precisely when we do not
          know which clinic the user means — several, or none yet. */}
      <Route
        path={CLINIC_PICKER_PATH}
        element={
          <RequireSession>
            <SelectClinicPage />
          </RequireSession>
        }
      />
      {/* Auth-required but names no clinic, so it cannot live under a slug:
          the clinic it creates does not exist yet. */}
      <Route
        path="/create-dispensary"
        element={
          <RequireSession>
            <CreateDispensaryPage />
          </RequireSession>
        }
      />
      {/* The platform surface. Global because it names EVERY clinic rather than
          one, so there is no slug it could sit under — the mirror image of the
          backend mounting `/api/admin/*` outside `/c/:clinicSlug`. `admin` was
          already in RESERVED_SLUGS before these routes existed, held back for
          exactly this. */}
      <Route
        path="/admin"
        element={
          <RequireSuperAdmin>
            <PlatformAdminLayout />
          </RequireSuperAdmin>
        }
      >
        <Route index element={<Navigate to="/admin/clinics" replace />} />
        <Route path="clinics" element={<PlatformClinicsPage />} />
        <Route path="clinics/:clinicId" element={<PlatformClinicDetailPage />} />
        <Route path="passwords" element={<PlatformPasswordResetPage />} />
        <Route path="super-admins" element={<PlatformSuperAdminsPage />} />
      </Route>
      {/* Public patient portal — no auth, no app shell, and global rather than
          per-clinic: the link is per-PATIENT, and the opaque token in the path
          is the entire credential until patient authentication lands. Which
          clinic it belongs to is the server's business, not the URL's. */}
      <Route path={PATIENT_PORTAL_ROUTE} element={<PatientPortalPage />} />
      {/* QR codes printed before clinics had slugs. They carry a raw clinic id;
          these routes look up the slug and forward to the new URL. */}
      <Route path="/p/counter/:clinicId" element={<LegacyPublicRedirect page="counter" />} />
      <Route path="/p/register/:clinicId" element={<LegacyPublicRedirect page="register" />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
